import { useEffect, useMemo, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';
import { Plus, Trash2, ImagePlus } from 'lucide-react';
import AuthImage from './AuthImage';
import { InfoButton } from './InfoModal';

function itemKey(it, fallbackIdx = 0) {
  if (!it) return `idx-${fallbackIdx}`;
  return String(
    it.id ||
      it.pool_item_id ||
      it.item_code ||
      `q:${String(it.question || it.text || '').trim().toLowerCase()}` ||
      `idx-${fallbackIdx}`
  );
}

function questionDedupeKey(it) {
  const code = String(it?.item_code || '').trim().toLowerCase();
  if (code) return `code:${code}`;
  const q = String(it?.question || it?.text || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  return q ? `q:${q}` : '';
}

function mergeDefaultAndTypeItems(pool, unitType) {
  const defaults = (pool?.ssDefault?.items || [])
    .map((it, i) =>
      normalizePoolItem(it, it.category || it.section || 'Default', 'SS_DEFAULT', i)
    )
    .filter(Boolean);
  const typeLabel = String(unitType || '').trim();
  const typeEntry = (pool?.types || []).find((t) => {
    const key = String(t.key || '').trim().toLowerCase();
    const label = String(t.label || '').trim().toLowerCase();
    const want = typeLabel.toLowerCase();
    return key === want || label === want;
  });
  const typeItems = (typeEntry?.items || [])
    .map((it, i) =>
      normalizePoolItem(
        it,
        it.category || it.section || typeEntry.label,
        'UNIT_TYPE',
        i
      )
    )
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const it of [...defaults, ...typeItems]) {
    const k = questionDedupeKey(it);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(it);
  }
  return out;
}

function normalizePoolItem(it, section, source, idx) {
  const question = String(it.question || it.text || '').trim();
  if (!question) return null;
  const instruction = it.instruction || it.info || '';
  let guidanceImages = it.guidance_images || it.guidanceImages || [];
  if (typeof guidanceImages === 'string') {
    try {
      guidanceImages = JSON.parse(guidanceImages);
    } catch {
      guidanceImages = [];
    }
  }
  if (!Array.isArray(guidanceImages)) guidanceImages = [];
  return {
    id: itemKey(it, idx),
    question,
    instruction,
    info: instruction,
    inspection_criteria: it.inspection_criteria || it.regulation_reference || '',
    method_tool: it.method_tool || '',
    category: section || it.category || '',
    section: section || it.section || it.category || '',
    source: source || it.source || 'SS_DEFAULT',
    unit_type: section || it.unit_type || null,
    is_required: it.is_required !== false,
    custom: !!it.custom,
    pool_item_id: it.pool_item_id || null,
    item_code: it.item_code || null,
    value_type: it.value_type || 'BOOL',
    unit: it.unit || null,
    pass_threshold: it.pass_threshold || null,
    pass_fail_mode: it.pass_fail_mode || 'MANUAL',
    require_photo: true,
    require_fail_evidence: true,
    guidance_images: guidanceImages,
  };
}

function defaultCheckedForSection(items, confirmedKeys) {
  const next = {};
  for (const it of items || []) {
    if (confirmedKeys.has(it.id)) continue;
    next[it.id] = true;
  }
  return next;
}

/**
 * Set Inspection List ?? pick by equipment type (pre-checked + Select),
 * edit Selected list, add private questions. Saved on unit → worker app.
 */
export default function UnitChecklistPicker({
  ssCode,
  buildingId,
  unitType = '',
  value = [],
  onChange,
  disabled = false,
}) {
  const [tab, setTab] = useState(unitType ? unitType : '__SS__');
  const [checked, setChecked] = useState({});
  const [privateForm, setPrivateForm] = useState({
    category: '',
    question: '',
    criteria: '',
    pass_fail_mode: 'MANUAL',
  });
  const poolListRef = useRef(null);
  const rootRef = useRef(null);
  const seededForRef = useRef('');

  const preserveScroll = (fn) => {
    const pool = poolListRef.current;
    const modal = rootRef.current?.closest?.('[data-unit-modal-scroll]');
    const poolTop = pool?.scrollTop ?? 0;
    const modalTop = modal?.scrollTop ?? 0;
    fn();
    requestAnimationFrame(() => {
      if (poolListRef.current) poolListRef.current.scrollTop = poolTop;
      const m = rootRef.current?.closest?.('[data-unit-modal-scroll]');
      if (m) m.scrollTop = modalTop;
    });
  };

  const { data: pool, isLoading } = useQuery({
    queryKey: ['checklist-pool-picker', ssCode, buildingId],
    queryFn: async () => {
      const res = await api.get('/checklist-sets/pool-for-picker', {
        params: { ssCode, buildingId },
        skipErrorToast: true,
      });
      return res.data?.data || { ssDefault: { items: [] }, types: [] };
    },
    enabled: !!ssCode,
    staleTime: 60_000,
  });

  const typeTabs = useMemo(() => {
    const list = [{ key: '__SS__', label: 'Default' }];
    for (const t of pool?.types || []) {
      const label = String(t.label || t.key || '').trim();
      if (/^(default|common\s*\/\s*base|ss\s*default)$/i.test(label)) continue;
      list.push({ key: t.key || t.label, label: t.label });
    }
    return list;
  }, [pool]);

  const activeSection = useMemo(() => {
    if (!pool) return { key: tab, label: 'Default', items: [] };
    if (tab === '__SS__') {
      return {
        key: '__SS__',
        label: 'Default',
        items: (pool.ssDefault?.items || [])
          .map((it, i) => normalizePoolItem(it, '', 'SS_DEFAULT', i))
          .filter(Boolean),
      };
    }
    const t = (pool.types || []).find((x) => (x.key || x.label) === tab);
    if (!t) return { key: tab, label: tab, items: [] };
    return {
      key: t.key || t.label,
      label: t.label,
      items: (t.items || [])
        .map((it, i) => normalizePoolItem(it, t.label, 'UNIT_TYPE', i))
        .filter(Boolean),
    };
  }, [pool, tab]);

  const confirmedKeys = useMemo(() => {
    const set = new Set();
    (value || []).forEach((it, i) => set.add(itemKey(it, i)));
    return set;
  }, [value]);

  // When type (or selected set) changes: pre-check all not-yet-selected questions
  const confirmedKeySig = useMemo(
    () =>
      (value || [])
        .map((it, i) => itemKey(it, i))
        .sort()
        .join('|'),
    [value]
  );

  useEffect(() => {
    if (!activeSection?.items?.length) {
      setChecked({});
      return;
    }
    setChecked(defaultCheckedForSection(activeSection.items, confirmedKeys));
  }, [tab, pool, confirmedKeySig, activeSection, confirmedKeys]);

  useEffect(() => {
    if (unitType && typeTabs.some((t) => t.key === unitType)) setTab(unitType);
  }, [unitType, typeTabs]);

  useEffect(() => {
    if (!pool || disabled) return;
    const sig = `${ssCode}|${String(unitType || '').trim()}`;
    if (seededForRef.current === sig) return;
    if ((value || []).length > 0) {
      seededForRef.current = sig;
      return;
    }
    const merged = mergeDefaultAndTypeItems(pool, unitType);
    seededForRef.current = sig;
    if (merged.length) onChange?.(merged);
  }, [pool, ssCode, unitType, disabled, value, onChange]);

  const selectableCount = useMemo(() => {
    let n = 0;
    for (const it of activeSection.items || []) {
      if (confirmedKeys.has(it.id)) continue;
      if (checked[it.id]) n += 1;
    }
    return n;
  }, [activeSection, checked, confirmedKeys]);

  const toggleCheck = (id) => {
    if (confirmedKeys.has(id)) return;
    preserveScroll(() => {
      setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
    });
  };

  const selectTypeChecked = () => {
    const next = [...(value || [])];
    const existing = new Set(next.map((it, i) => itemKey(it, i)));
    for (const it of activeSection.items || []) {
      if (!checked[it.id]) continue;
      if (existing.has(it.id)) continue;
      next.push({ ...it });
      existing.add(it.id);
    }
    onChange?.(next);
  };

  const addPrivate = () => {
    const question = privateForm.question.trim();
    const category = privateForm.category.trim() || 'Private';
    const criteria = privateForm.criteria.trim();
    if (!question) return;
    const mode = privateForm.pass_fail_mode === 'AUTO' ? 'AUTO' : 'MANUAL';
    const item = {
      id: `private-${Date.now()}`,
      question,
      instruction: '',
      info: '',
      inspection_criteria: criteria,
      pass_threshold: mode === 'AUTO' ? criteria : null,
      pass_fail_mode: mode,
      value_type: mode === 'AUTO' ? 'NUM' : 'BOOL',
      show_measurement: mode === 'AUTO',
      method_tool: '',
      category,
      section: category,
      source: 'PRIVATE',
      unit_type: null,
      is_required: true,
      custom: true,
      require_photo: true,
      require_fail_evidence: true,
      guidance_images: [],
    };
    onChange?.([...(value || []), item]);
    setPrivateForm({ category: '', question: '', criteria: '', pass_fail_mode: 'MANUAL' });
  };

  const removeAt = (idx) => {
    const next = (value || []).slice();
    next.splice(idx, 1);
    onChange?.(next);
  };

  const updateText = (idx, text) => {
    const next = (value || []).map((it, i) => (i === idx ? { ...it, question: text } : it));
    onChange?.(next);
  };

  const updateField = (idx, patch) => {
    const next = (value || []).map((it, i) => {
      if (i !== idx) return it;
      const merged = { ...it, ...patch };
      if (patch.instruction !== undefined) merged.info = patch.instruction;
      if (patch.info !== undefined) merged.instruction = patch.info;
      if (patch.category !== undefined) {
        merged.section = patch.category;
      }
      return merged;
    });
    onChange?.(next);
  };

  const uploadGuidanceImage = async (idx, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('type', 'checklist-guidance');
      fd.append('file', file);
      const res = await api.post('/files/upload', fd, {
        headers: { 'X-Upload-Type': 'checklist-guidance' },
      });
      const filename = res.data?.filename;
      if (!filename) throw new Error('No filename');
      const cur = value[idx] || {};
      const imgs = Array.isArray(cur.guidance_images) ? [...cur.guidance_images] : [];
      imgs.push({ filename, caption: '', url: res.data?.url || `/api/files/${filename}` });
      updateField(idx, { guidance_images: imgs });
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || e.message || 'Upload failed');
    }
  };

  const selectedGroups = useMemo(() => {
    const groups = [];
    const map = new Map();
    (value || []).forEach((it, idx) => {
      const sec = String(it.section || it.category || it.unit_type || 'Selected').trim() || 'Selected';
      if (!map.has(sec)) {
        const g = { label: sec, items: [] };
        map.set(sec, g);
        groups.push(g);
      }
      map.get(sec).items.push({ it, idx });
    });
    return groups;
  }, [value]);

  return (
    <div ref={rootRef} className="space-y-5 border border-gray-200 rounded-xl p-4 bg-gray-50/60">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h3 className="text-xl font-semibold text-gray-900">Set Inspection List</h3>
        <InfoButton title="Set Inspection List">
          <div className="space-y-3 text-sm">
            <p>
              This list starts with <strong>Default</strong> questions plus this unit type. Duplicate questions
              appear once. <strong>Save</strong> stores the list on this unit for the worker app.
            </p>
            <p>
              Add private questions with category, text, measurement criteria, and auto/manual Pass–Fail.
            </p>
            <p>
              On site, every question supports camera and upload. Fail requires a reason plus a photo.
            </p>
          </div>
        </InfoButton>
      </div>

      <section className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/70 p-4">
        <p className="text-lg font-semibold text-emerald-900">
          Selected questions for this unit ({(value || []).length})
        </p>
        {(value || []).length === 0 && (
          <p className="text-xs text-emerald-800/70">
            No questions yet ?? default and this unit type will fill in when the pool loads.
          </p>
        )}
        {selectedGroups.map((g) => (
          <div key={g.label} className="space-y-1.5">
            <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-800/80">{g.label}</p>
            {g.items.map(({ it, idx }) => (
              <div key={itemKey(it, idx)} className="bg-white border border-emerald-100 rounded-lg px-2 py-2 space-y-1.5">
                <div className="flex gap-2 items-start">
                  <span className="shrink-0 w-6 text-xs font-bold text-emerald-700 pt-1 tabular-nums">
                    {idx + 1}.
                  </span>
                  {it.custom ? (
                    <input
                      className="flex-1 text-sm border-0 focus:ring-0 px-1 py-0.5 font-medium"
                      value={it.question || ''}
                      readOnly={disabled}
                      onChange={(e) => updateText(idx, e.target.value)}
                    />
                  ) : (
                    <p className="flex-1 text-sm font-medium text-gray-900 px-1 py-0.5">{it.question}</p>
                  )}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => removeAt(idx)}
                      className="p-1 text-red-500 hover:text-red-700"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {it.custom && (
                  <input
                    disabled={disabled}
                    className="w-full text-xs border border-gray-100 rounded px-2 py-1.5 text-slate-700 bg-slate-50"
                    placeholder="Category"
                    value={it.category || it.section || ''}
                    onChange={(e) => updateField(idx, { category: e.target.value })}
                  />
                )}
                <textarea
                  rows={2}
                  disabled={disabled}
                  className="w-full text-xs border border-gray-100 rounded px-2 py-1.5 text-slate-700 bg-slate-50"
                  placeholder="Info ?? on-site how-to"
                  value={it.instruction || it.info || ''}
                  onChange={(e) => updateField(idx, { instruction: e.target.value })}
                />
                <div className="flex flex-wrap gap-2 items-center text-[11px] text-slate-600">
                  <span className="px-1.5 py-0.5 rounded bg-slate-100">
                    {(it.pass_fail_mode || 'MANUAL') === 'AUTO' ? 'Auto Pass/Fail' : 'Manual Pass/Fail'}
                  </span>
                  {(it.inspection_criteria || it.pass_threshold) && (
                    <span className="truncate max-w-full">
                      Criteria: {it.inspection_criteria || it.pass_threshold}
                    </span>
                  )}
                </div>
                {it.custom && (
                  <input
                    disabled={disabled}
                    className="w-full text-xs border border-gray-100 rounded px-2 py-1.5 text-slate-700 bg-slate-50"
                    placeholder="Measurement criteria / pass when"
                    value={it.inspection_criteria || it.pass_threshold || ''}
                    onChange={(e) =>
                      updateField(idx, {
                        inspection_criteria: e.target.value,
                        pass_threshold: (it.pass_fail_mode || 'MANUAL') === 'AUTO' ? e.target.value : it.pass_threshold,
                      })
                    }
                  />
                )}
                <div className="flex flex-wrap gap-2 items-center">
                  {(it.guidance_images || []).map((gImg, gi) => (
                    <div key={gImg.filename || gi} className="relative w-16 h-16">
                      <AuthImage
                        filename={gImg.filename}
                        alt={gImg.caption || 'Guidance'}
                        className="w-16 h-16 object-cover rounded border"
                      />
                      {!disabled && (
                        <button
                          type="button"
                          className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-600 text-white text-[10px] leading-none"
                          onClick={() => {
                            const imgs = (it.guidance_images || []).filter((_, j) => j !== gi);
                            updateField(idx, { guidance_images: imgs });
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  {!disabled && (
                    <label className="inline-flex items-center gap-1 px-2 py-1.5 rounded border border-dashed border-gray-300 text-[11px] text-gray-600 cursor-pointer hover:bg-gray-50">
                      <ImagePlus className="w-3.5 h-3.5" />
                      Guidance photo
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          uploadGuidanceImage(idx, f);
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </section>

      {!disabled && !unitType && (
        <section className="space-y-3 rounded-xl border border-sky-200 bg-sky-50/70 p-4">
          <p className="text-lg font-semibold text-sky-900">Equipment type pools</p>
          <div className="flex flex-wrap gap-1.5">
            {typeTabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  preserveScroll(() => setTab(t.key));
                }}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${
                  tab === t.key
                    ? 'bg-sky-700 text-white border-sky-700'
                    : 'bg-white text-sky-900 border-sky-200 hover:bg-sky-100'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div
            ref={poolListRef}
            className="bg-white border border-sky-100 rounded-lg max-h-56 overflow-y-auto divide-y divide-sky-50"
          >
            {isLoading && <p className="p-3 text-xs text-gray-500">Loading checklist pool…</p>}
            {!isLoading && (activeSection.items || []).length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">No questions in this type yet.</p>
            )}
            {!isLoading &&
              (activeSection.items || []).map((it) => {
                const already = confirmedKeys.has(it.id);
                return (
                  <label
                    key={it.id}
                    className={`flex gap-2 items-start px-3 py-2 text-sm ${
                      already ? 'opacity-45' : 'hover:bg-sky-50/80'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 shrink-0"
                      disabled={already || disabled}
                      checked={already || !!checked[it.id]}
                      onMouseDown={(e) => e.preventDefault()}
                      onChange={() => toggleCheck(it.id)}
                    />
                    <span className="min-w-0">
                      <span className="text-gray-800 leading-snug block">{it.question}</span>
                      {(it.instruction || it.info) && (
                        <span className="text-[11px] text-slate-500 leading-snug mt-0.5 line-clamp-2 block">
                          Info: {it.instruction || it.info}
                        </span>
                      )}
                      {already && (
                        <span className="text-[10px] text-amber-700 font-medium">Already in Selected list</span>
                      )}
                    </span>
                  </label>
                );
              })}
          </div>

          <button
            type="button"
            disabled={!selectableCount || disabled}
            onClick={selectTypeChecked}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-700 text-white text-sm font-semibold disabled:opacity-40 hover:bg-sky-800"
          >
            <Plus className="w-4 h-4" />
            Select {activeSection.label} ({selectableCount})
          </button>
        </section>
      )}

      {!disabled && (
        <section className="space-y-3 rounded-xl border border-violet-200 bg-violet-50/70 p-4">
          <p className="text-lg font-semibold text-violet-900">Add private question</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">1. Category</label>
              <input
                value={privateForm.category}
                onChange={(e) => setPrivateForm((p) => ({ ...p, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="e.g. Condition, Function"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Pass / Fail mode</label>
              <select
                value={privateForm.pass_fail_mode}
                onChange={(e) => setPrivateForm((p) => ({ ...p, pass_fail_mode: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="MANUAL">Manual Pass / Fail buttons</option>
                <option value="AUTO">Auto from measurement criteria</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">2. Question</label>
              <input
                value={privateForm.question}
                onChange={(e) => setPrivateForm((p) => ({ ...p, question: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="What should the inspector check?"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                3. Measurement criteria {privateForm.pass_fail_mode === 'AUTO' ? '(required for auto)' : '(optional)'}
              </label>
              <input
                value={privateForm.criteria}
                onChange={(e) => setPrivateForm((p) => ({ ...p, criteria: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder={
                  privateForm.pass_fail_mode === 'AUTO'
                    ? 'e.g. Pressure ≥ 200 kPa / reading within manufacturer range'
                    : 'Pass when / ground for the inspector'
                }
              />
            </div>
          </div>
          <button
            type="button"
            onClick={addPrivate}
            disabled={
              !privateForm.question.trim() ||
              (privateForm.pass_fail_mode === 'AUTO' && !privateForm.criteria.trim())
            }
            className="px-3 py-2 rounded-lg bg-violet-700 text-white text-sm font-semibold disabled:opacity-40 hover:bg-violet-800"
          >
            Add private question
          </button>
        </section>
      )}
    </div>
  );
}

export { itemKey, normalizePoolItem, mergeDefaultAndTypeItems };
